import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { IsActiveMatchOptions, Router } from '@angular/router';

import { MenuItem } from '../services/side-menu.service';

@Component({
  selector: 'app-menu-items',
  templateUrl: './menu-items.component.html',
  styleUrls: ['./menu-items.component.scss'],
  standalone: false,
})
export class MenuItemsComponent {
  @Input() iconOnlyMode = true;
  @Input() menuItems: MenuItem[] = [];
  @Input() searchValue = '';
  @Output() onClicked = new EventEmitter<MenuItem>();
  @Output() onExpanded = new EventEmitter<MenuItem>();

  /** Positioned at cursor for MatMenuTrigger (see documents context menu pattern) */
  menuTopLeftPosition = { x: '0px', y: '0px' };
  private contextMenuLink: string | null = null;

  @ViewChild('navLinkContextMenuTrigger', { read: MatMenuTrigger })
  navLinkContextMenuTrigger: MatMenuTrigger;

  constructor(private router: Router) {}

  activeMatchOptions: IsActiveMatchOptions = {
    matrixParams: 'ignored',
    queryParams: 'ignored',
    paths: 'subset',
    fragment: 'exact',
  };

  activeMatchOptionsExact: IsActiveMatchOptions = {
    matrixParams: 'ignored',
    queryParams: 'ignored',
    paths: 'subset',
    fragment: 'exact',
  };

  itemSelect(menuItem: MenuItem) {
    if (this.iconOnlyMode) {
      if (!menuItem.children) {
        this.onClicked.emit(menuItem);
      }
    } else {
      this.onClicked.emit(menuItem);
    }
  }

  filter(str: string) {
    return str.toLowerCase().search(this.searchValue.toLowerCase()) > -1;
  }

  filterMatch(mi: MenuItem): boolean {
    const childMatchesDeep = (nodes: MenuItem[] | undefined): boolean => {
      if (!nodes?.length) {
        return false;
      }
      return nodes.some(
        (c) =>
          this.filter(c.face) ||
          (c.children?.length ? childMatchesDeep(c.children) : false)
      );
    };
    return this.filter(mi.face) || childMatchesDeep(mi.children);
  }

  /** Icon-only mode has no room for the CORE/SAAS header row, so show their
   * children directly in the rail instead of a blank dropdown-trigger icon. */
  get displayItems(): MenuItem[] {
    if (!this.iconOnlyMode) {
      return this.menuItems;
    }
    const flattened: MenuItem[] = [];
    this.menuItems.forEach((item) => {
      if ((item.key === 'CORE' || item.key === 'SAAS') && item.children) {
        flattened.push(...item.children);
      } else {
        flattened.push(item);
      }
    });
    return flattened;
  }

  beforeExpand(item: MenuItem) {
    this.onExpanded.emit(item);
  }

  /** Collapse sibling nested groups under the same parent (e.g. Authorization vs Setup under CORE) */
  beforeExpandNested(parent: MenuItem, item: MenuItem) {
    this.onExpanded.emit(item);
    parent.children?.forEach((sibling) => {
      if (sibling !== item) {
        sibling.expanded = false;
      }
    });
  }

  isVisible(moduleName: string): boolean {
    return !!moduleName;
  }

  /** Menu data uses `link` or shared constants (any casing) */
  isLinkType(item: MenuItem): boolean {
    return String(item.type).toLowerCase() === 'link';
  }

  bulkFilter(menuItem: MenuItem) {
    let foundStatus = false;
    menuItem.children.forEach((item) => {
      if (this.filter(item.searchKey)) {
        foundStatus = true;
      }
    });
    if (foundStatus && this.searchValue.length > 0) {
      menuItem.expanded = true;
    } else {
      menuItem.expanded = false;
    }
    return foundStatus;
  }

  collapseAll() {
    this.menuItems.forEach((item) => {
      item.expanded = false;
    });
  }
  /** Filled icons when route is active (root or any child), outlined otherwise */
  iconFontSet(menuItem: MenuItem): string {
    return this.isMenuOrChildActive(menuItem)
      ? 'material-icons'
      : 'material-icons-outlined';
  }

  isRouteActive(link: string | undefined): boolean {
    if (!link) {
      return false;
    }
    const path = this.router.url.split('?')[0];
    if (path === link) {
      return true;
    }
    return path.startsWith(link + '/');
  }

  isMenuOrChildActive(menuItem: MenuItem): boolean {
    if (this.isRouteActive(menuItem.link)) {
      return true;
    }
    return menuItem.children?.some((c) => this.isRouteActive(c.link)) ?? false;
  }

  onNavLinkContextMenu(
    event: MouseEvent,
    link: string | undefined,
    loading: boolean
  ): void {
    if (!link || loading) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuLink = link;
    this.menuTopLeftPosition.x = `${event.clientX}px`;
    this.menuTopLeftPosition.y = `${event.clientY}px`;
    this.navLinkContextMenuTrigger?.openMenu();
  }

  openContextMenuLinkInNewTab(): void {
    if (!this.contextMenuLink) {
      return;
    }
    const url = this.router.serializeUrl(
      this.router.parseUrl(this.contextMenuLink)
    );
    window.open(url, '_blank', 'noopener,noreferrer');
    this.navLinkContextMenuTrigger?.closeMenu();
    this.contextMenuLink = null;
  }
}
